import { useEffect, useState } from 'react';
import { Box, LinearProgress, Typography, keyframes } from '@mui/material';
import { MENU_LATERAL_RAIL_WIDTH } from '../menu-lateral/MenuLateral';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const DURACAO_FADE_MS = 900;

interface ITelaCarregandoProps {
  open: boolean;
  message?: string;
  // Controla o fade suave de saída ao revelar o conteúdo por trás desta tela — ver
  // fase "transitioning" em Login.tsx.
  fading?: boolean;
  // Quando false, cobre apenas o container pai (que deve ter position: relative)
  // em vez da tela inteira — usado para restringir o loading a uma área específica.
  fullScreen?: boolean;
  // Deixa o rail do menu lateral (MenuLateral) de fora da área coberta.
  excluirMenuLateral?: boolean;
  // Cor de fundo do overlay — permite uma versão mais suave para telas de carregamento
  // simples (ex.: carregar dados de um produto), reservando o preto mais forte para
  // operações de escrita (salvar/apagar).
  backgroundColor?: string;
  // Mostra só o ícone girando, sem mensagem nem barra de progresso — usado em áreas
  // menores (ex.: listagens) onde o texto ocuparia espaço demais.
  apenasIcone?: boolean;
}

export const TelaCarregando: React.FC<ITelaCarregandoProps> = ({
  open,
  message = 'Carregando...',
  fading = false,
  fullScreen = true,
  excluirMenuLateral = false,
  backgroundColor = 'rgba(0, 0, 0, 0.88)',
  apenasIcone = false,
}) => {
  const [montado, setMontado] = useState(open);
  const [entrou, setEntrou] = useState(false);

  // Sem isso, a tela aparecia/sumia de uma vez (sem transição) toda vez que `open`
  // mudava, dando impressão de travamento em requisições rápidas. Aqui ela some do
  // DOM só depois que a transição de opacidade termina.
  useEffect(() => {
    if (open) {
      setMontado(true);
      const raf = requestAnimationFrame(() => setEntrou(true));
      return () => cancelAnimationFrame(raf);
    }

    setEntrou(false);
    const timeout = setTimeout(() => setMontado(false), DURACAO_FADE_MS);
    return () => clearTimeout(timeout);
  }, [open]);

  if (!montado) return null;

  const visivel = entrou && !fading;

  return (
    <Box
      sx={{
        position: fullScreen ? 'fixed' : 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: fullScreen && excluirMenuLateral ? { xs: 0, sm: MENU_LATERAL_RAIL_WIDTH } : 0,
        zIndex: fullScreen ? (theme) => theme.zIndex.modal + 10 : (theme) => theme.zIndex.fab,
        backgroundColor,
        backdropFilter: 'blur(6px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: visivel ? 1 : 0,
        transition: `opacity ${fading ? 1100 : DURACAO_FADE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
        pointerEvents: visivel ? 'auto' : 'none',
      }}
    >
      <Box
        component="img"
        src="/img/torqsiac-loading.png"
        alt="Carregando"
        loading="eager"
        sx={{
          width: 88,
          height: 88,
          filter: 'brightness(0) invert(1)',
          animation: `${spin} 3.6s linear infinite`,
        }}
      />

      {!apenasIcone && (
        <>
          <Typography
            sx={{
              mt: 3,
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '1rem',
              letterSpacing: 0.3,
            }}
          >
            {message}
          </Typography>

          <Box sx={{ position: 'absolute', bottom: 40, width: 280 }}>
            <LinearProgress
              sx={{
                height: 4,
                borderRadius: 999,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#ffffff',
                  borderRadius: 999,
                },
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
